import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../../config';
import { format } from 'date-fns';
import StateMunicipalityDropdown from './StateMunicipalityDropdown';
import Swal from 'sweetalert2';

const AddPlatePopup = ({ isOpen, onRequestClose, loadPlateRenewalData }) => {
  const [municipalities, setMunicipalities] = useState([]);
  const [plateTypeOptions, setPlateTypeOptions] = useState([]);
  const [formPlateNo, setFormPlateNo] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [formPlateType, setFormPlateType] = useState(null);
  const [formExpiration, setFormExpiration] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetch(API_URL + '/municipalities')
      .then((response) => response.json())
      .then((data) => setMunicipalities(data))
      .catch((error) => console.error('Error fetching municipalities:', error));
  }, []);

  const handleStateChange = (selectedOption) => {
    setSelectedMunicipality(selectedOption);
    setFormPlateType(null);
    setPlateTypeOptions([]);
    setFormExpiration(null);
    if (selectedOption) fetchPlateTypes(selectedOption.value);
  };

  const fetchPlateTypes = (municipalityId) => {
    fetch(API_URL + `/plate-types?state_id=${municipalityId}`)
      .then((response) => response.json())
      .then((data) => {
        const options = data.map((plateType) => ({ value: plateType.id, label: plateType.name }));
        setPlateTypeOptions(options);
      })
      .catch((error) => console.error('Error fetching plate types:', error));
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formPlateNo) validationErrors.plateNo = 'Please enter the plate number.';
    if (!selectedMunicipality) validationErrors.municipality = 'Please select a county.';
    if (!formExpiration) validationErrors.expiration = 'Please select the expiration date.';
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleAddPlate = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Please fix the form errors.' });
      return;
    }

    const authToken = localStorage.getItem('token');
    if (!authToken) {
      Swal.fire({ icon: 'warning', title: 'Unauthorized', text: 'Please login to continue.' });
      return;
    }

    const newPlateRenewal = {
      plate_number: formPlateNo,
      municipality_id: selectedMunicipality.value,
      expiration_date: formExpiration.toISOString(),
    };

    fetch(API_URL + '/plate-renewals', {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(newPlateRenewal),
    })
      .then(async (response) => {
        if (response.status === 201) {
          Swal.fire({ icon: 'success', title: 'Success', text: 'Plate renewal data saved successfully' });
          setFormPlateNo('');
          setFormPlateType(null);
          setFormExpiration(null);
          onRequestClose();
          loadPlateRenewalData();
        } else if (response.status === 422) {
          const errorData = await response.json();
          const errorMessages = Object.values(errorData.errors).flat();
          Swal.fire({ icon: 'error', title: 'Validation Errors', text: errorMessages.join(', ') });
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save plate renewal data' });
        }
      })
      .catch((error) => {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save plate renewal data' });
      });
  };

  const inputClasses = "tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-900 tw-outline-none focus:tw-border-blue-500 focus:tw-ring-2 focus:tw-ring-blue-500/20 tw-transition-all";
  const errorClasses = "tw-text-xs tw-text-red-600 tw-mt-1";

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-lg tw-w-full tw-mx-4 tw-max-h-[90vh] tw-overflow-y-auto tw-p-6 tw-outline-none"
      overlayClassName="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-[9999]"
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
        <h2 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-m-0">Add Plate</h2>
        <button onClick={onRequestClose} className="tw-text-gray-400 hover:tw-text-gray-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-1">
          <FontAwesomeIcon icon={faTimes} className="tw-text-lg" />
        </button>
      </div>

      <div className="tw-space-y-4">
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">State & County</label>
          <StateMunicipalityDropdown
            data={municipalities}
            selectedMunicipality={selectedMunicipality}
            setSelectedMunicipality={handleStateChange}
          />
          {errors.municipality && <p className={errorClasses}>{errors.municipality}</p>}
        </div>

        <div>
          <label htmlFor="plateNoInput" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">License Plate Number</label>
          <input
            type="text"
            className={inputClasses}
            id="plateNoInput"
            placeholder="ABC123"
            value={formPlateNo}
            onChange={(e) => setFormPlateNo((e.target.value) ? e.target.value.replace(/[^0-9a-zA-Z]+/ig, "") : '')}
          />
          {errors.plateNo && <p className={errorClasses}>{errors.plateNo}</p>}
        </div>

        <div>
          <label htmlFor="expirationInput" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">Expiration</label>
          <DatePicker
            id="expirationInput"
            className={inputClasses}
            selected={formExpiration}
            onKeyDown={(e) => { e.preventDefault() }}
            onChange={(date) => setFormExpiration(new Date(date.getFullYear(), date.getMonth() + 1, 0, 0))}
            placeholderText="Select expiration date"
            showMonthYearPicker
            dateFormat="MMM yyyy"
            minDate={new Date(new Date().setDate(new Date().getDate() - 45))}
          />
          {errors.expiration && <p className={errorClasses}>{errors.expiration}</p>}
        </div>
      </div>

      <div className="tw-mt-6 tw-flex tw-justify-end">
        <button
          onClick={handleAddPlate}
          className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-px-6 tw-py-2.5 tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors"
        >
          Add Plate
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </div>
    </Modal>
  );
};

export default AddPlatePopup;
